import { CheckCircle2, Clock4, Mail, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { organizerApi } from "../../api/client";
import Footer from "../../components/feature/Footer";
import Navbar from "../../components/feature/Navbar";
import type { OrganizerApplication } from "../../types/api";

export default function OrganizerStatusPage() {
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get("email") ?? "";

  const [email, setEmail] = useState(initialEmail);
  const [submittedEmail, setSubmittedEmail] = useState(initialEmail);
  const [application, setApplication] = useState<OrganizerApplication | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(initialEmail));

  useEffect(() => {
    if (!initialEmail) return;
    void check(initialEmail);
  }, [initialEmail]);

  const check = async (value: string) => {
    setError(null);
    setIsLoading(true);
    setSubmittedEmail(value);
    try {
      const result = await organizerApi.status(value);
      setApplication(result.application);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Could not load status");
      setApplication(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (submitEvent: React.FormEvent<HTMLFormElement>) => {
    submitEvent.preventDefault();
    if (email.trim()) void check(email.trim().toLowerCase());
  };

  return (
    <div className="min-h-screen bg-surface-muted">
      <Navbar />

      <section className="pb-20 pt-28 sm:pt-32 lg:pt-36">
        <div className="section-frame">
          <div className="mx-auto max-w-2xl">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-foreground sm:text-5xl">Application status</h1>
              <p className="mt-3 text-sm text-muted-foreground">
                Check on your exhibitor application using the email you signed up with.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="mt-8 flex flex-col gap-3 rounded-2xl border border-line bg-surface p-5 shadow-[0_18px_45px_-24px_rgba(15,18,23,0.18)] sm:flex-row sm:items-center"
            >
              <div className="flex flex-1 items-center gap-2 rounded-xl border border-line bg-surface px-3 py-2.5">
                <Mail size={16} className="shrink-0 text-[#9aa3b2]" />
                <input
                  type="email"
                  required
                  placeholder="you@yourbrand.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-[#9aa3b2]"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="rounded-xl bg-[#0b1220] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-black dark:bg-[var(--pp-gold)] dark:text-[#0b1220] dark:hover:bg-[#e2a503] disabled:opacity-60"
              >
                {isLoading ? "Checking..." : "Check status"}
              </button>
            </form>

            {error && (
              <div className="mt-4 rounded-xl border border-[rgba(220,38,38,0.18)] bg-[#fef2f2] dark:bg-[rgba(239,68,68,0.14)] px-4 py-3 text-sm font-semibold text-[#b91c1c]">
                {error}
              </div>
            )}

            {!isLoading && submittedEmail && !error && application === null && (
              <div className="mt-6 rounded-2xl border border-dashed border-line bg-surface px-5 py-8 text-center">
                <div className="text-sm font-semibold text-foreground">
                  We don't have an application on file for {submittedEmail}.
                </div>
                <Link
                  to="/exhibitor/signup"
                  className="mt-3 inline-flex items-center justify-center rounded-xl bg-[var(--pp-gold)] px-5 py-2.5 text-sm font-bold text-foreground"
                >
                  Submit an application
                </Link>
              </div>
            )}

            {application && <StatusCard application={application} />}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function StatusCard({ application }: { application: OrganizerApplication }) {
  if (application.status === "pending") {
    return (
      <Card
        tone="warn"
        icon={<Clock4 size={22} />}
        title="Application under review"
        body="Our team is reviewing your details. You'll get an email once a decision has been made — usually within 1–2 business days."
      />
    );
  }

  if (application.status === "approved") {
    return (
      <Card
        tone="success"
        icon={<CheckCircle2 size={22} />}
        title="You're approved!"
        body="Sign in to your exhibitor dashboard with the email and password from your application."
        action={
          <Link
            to="/admin/login"
            className="inline-flex items-center justify-center rounded-xl bg-[#0b1220] px-5 py-2.5 text-sm font-bold text-white hover:bg-black dark:bg-[var(--pp-gold)] dark:text-[#0b1220] dark:hover:bg-[#e2a503]"
          >
            Sign in to dashboard
          </Link>
        }
      />
    );
  }

  return (
    <Card
      tone="danger"
      icon={<XCircle size={22} />}
      title="Application not approved"
      body={
        application.rejectionReason
          ? `Reason: ${application.rejectionReason}`
          : "Please contact our team if you'd like more details, or update your information and re-apply."
      }
    />
  );
}

function Card({
  tone,
  icon,
  title,
  body,
  action,
}: {
  tone: "warn" | "success" | "danger";
  icon: React.ReactNode;
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  const styles =
    tone === "warn"
      ? "border-[rgba(217,119,6,0.25)] bg-[#fffbeb] dark:bg-[rgba(245,158,11,0.14)] text-[#92400e]"
      : tone === "success"
        ? "border-[rgba(22,163,74,0.25)] bg-[#f0fdf4] dark:bg-[rgba(16,185,129,0.14)] text-[#166534]"
        : "border-[rgba(220,38,38,0.25)] bg-[#fef2f2] dark:bg-[rgba(239,68,68,0.14)] text-[#991b1b]";

  return (
    <div className={`mt-6 flex items-start gap-4 rounded-2xl border px-5 py-5 ${styles}`}>
      <div className="shrink-0">{icon}</div>
      <div className="flex-1">
        <div className="text-base font-bold">{title}</div>
        <p className="mt-1 text-sm leading-6">{body}</p>
        {action && <div className="mt-3">{action}</div>}
      </div>
    </div>
  );
}
